import React, { useState, useEffect } from 'react';
import { Zap, Phone, X, Loader2, Building, Wrench, Send, Image, Upload, Save, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';
import { SERVICE_ICONS, DEFAULT_SERVICES } from '../../utils/constants';

const AdminSiteContent = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const data = await api('/api/site-content');
      setContent(data);
    } catch (err) {
      toast.error('Failed to load site content');
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (section, data) => {
    setSaving(true);
    try {
      await api('/api/site-content', {
        method: 'PUT',
        body: JSON.stringify({ [section]: data })
      });
      toast.success('Content saved!');
      loadContent();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const tabs = [
    { id: 'hero', label: 'Hero Section', icon: Zap },
    { id: 'company', label: 'Company Info', icon: Building },
    { id: 'services', label: 'Services', icon: Wrench },
    { id: 'cta', label: 'CTA Section', icon: Send }
  ];

  return (
    <div data-testid="admin-site-content" className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-white">Site Content Manager</h1>
      <p className="text-gray-400">Edit the text and content that appears on your website.</p>

      {/* Logo Editor - at the top */}
      <div className="card p-6 bg-gray-900 border-gray-800 space-y-4" data-testid="logo-editor">
        <h2 className="font-heading text-xl font-bold text-white">Site Logo</h2>
        <p className="text-sm text-gray-400">Upload your company logo by providing the image URL.</p>
        <div className="flex items-center gap-4">
          {content?.logo_url && <img src={content.logo_url} alt="Logo" className="h-16 object-contain bg-gray-800 p-2 rounded" />}
          <div className="flex-1 flex gap-2">
            <input type="url" placeholder="Logo Image URL" className="input flex-1" id="logo-url-input" defaultValue={content?.logo_url || ''} data-testid="logo-url-input" />
            <button onClick={async () => {
              const url = document.getElementById('logo-url-input').value;
              setSaving(true);
              try { await api('/api/site-content', { method: 'PUT', body: JSON.stringify({ logo_url: url }) }); toast.success('Logo updated!'); loadContent(); }
              catch (err) { toast.error(err.message); } finally { setSaving(false); }
            }} className="btn-primary" data-testid="save-logo-btn">{saving ? 'Saving...' : 'Save Logo'}</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid={`cms-tab-${tab.id}`}
            >
              <TabIcon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Hero Section Editor */}
      {activeTab === 'hero' && (
        <HeroEditor data={content?.hero} onSave={(data) => saveSection('hero', data)} saving={saving} />
      )}

      {/* Company Info Editor */}
      {activeTab === 'company' && (
        <CompanyEditor data={content?.company} onSave={(data) => saveSection('company', data)} saving={saving} />
      )}

      {/* Services Editor */}
      {activeTab === 'services' && (
        <ServicesEditor data={content?.services} onSave={(data) => saveSection('services', data)} saving={saving} />
      )}

      {/* CTA Section Editor */}
      {activeTab === 'cta' && (
        <CTAEditor data={content?.cta_section} onSave={(data) => saveSection('cta_section', data)} saving={saving} />
      )}
    </div>
  );
};

const HeroEditor = ({ data, onSave, saving }) => {
  const [form, setForm] = useState({
    title_line1: data?.title_line1 || '',
    title_line2: data?.title_line2 || '',
    description: data?.description || '',
    cta_button: data?.cta_button || '',
    secondary_button: data?.secondary_button || ''
  });

  return (
    <div className="card p-6 bg-gray-900 border-gray-800 space-y-4" data-testid="hero-editor">
      <h2 className="font-heading text-xl font-bold text-white">Hero Section</h2>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Title Line 1</label>
        <input className="input" value={form.title_line1} onChange={(e) => setForm({ ...form, title_line1: e.target.value })} data-testid="hero-title1-input" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Title Line 2 (Red text)</label>
        <input className="input" value={form.title_line2} onChange={(e) => setForm({ ...form, title_line2: e.target.value })} data-testid="hero-title2-input" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
        <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="hero-desc-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Primary Button Text</label>
          <input className="input" value={form.cta_button} onChange={(e) => setForm({ ...form, cta_button: e.target.value })} data-testid="hero-cta-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Secondary Button Text</label>
          <input className="input" value={form.secondary_button} onChange={(e) => setForm({ ...form, secondary_button: e.target.value })} data-testid="hero-secondary-input" />
        </div>
      </div>
      <button onClick={() => onSave(form)} disabled={saving} className="btn-primary" data-testid="save-hero-btn">
        {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
        Save Hero Section
      </button>
    </div>
  );
};

const CompanyEditor = ({ data, onSave, saving }) => {
  const [form, setForm] = useState({
    phone: data?.phone || '',
    tagline: data?.tagline || '',
    copyright: data?.copyright || '',
    location: data?.location || '',
    ein: data?.ein || '',
    tax_address: data?.tax_address || '',
    tax_city_state_zip: data?.tax_city_state_zip || '',
  });

  return (
    <div className="card p-6 bg-gray-900 border-gray-800 space-y-4" data-testid="company-editor">
      <h2 className="font-heading text-xl font-bold text-white">Company Information</h2>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Phone Number</label>
        <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="company-phone-input" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Tagline</label>
        <input className="input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="company-tagline-input" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Location</label>
        <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="company-location-input" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Copyright Text</label>
        <input className="input" value={form.copyright} onChange={(e) => setForm({ ...form, copyright: e.target.value })} data-testid="company-copyright-input" />
      </div>

      {/* Tax / IRS settings - used by 1099-NEC PDF generator */}
      <div className="pt-4 mt-4 border-t border-gray-800">
        <h3 className="text-sm font-bold text-orange-300 tracking-widest mb-3">TAX / IRS SETTINGS</h3>
        <p className="text-xs text-gray-500 mb-4">These fields are used when generating 1099-NEC year-end statements for 1099 contractors.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">EIN (Federal Tax ID)</label>
            <input
              className="input font-mono"
              placeholder="XX-XXXXXXX"
              value={form.ein}
              onChange={(e) => setForm({ ...form, ein: e.target.value })}
              data-testid="company-ein-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Tax Address</label>
            <input
              className="input"
              placeholder="Street address"
              value={form.tax_address}
              onChange={(e) => setForm({ ...form, tax_address: e.target.value })}
              data-testid="company-tax-address-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">City, State, ZIP</label>
            <input
              className="input"
              placeholder="New York, NY 10001"
              value={form.tax_city_state_zip}
              onChange={(e) => setForm({ ...form, tax_city_state_zip: e.target.value })}
              data-testid="company-tax-csz-input"
            />
          </div>
        </div>
      </div>

      <button onClick={() => onSave(form)} disabled={saving} className="btn-primary" data-testid="save-company-btn">
        {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
        Save Company Info
      </button>
    </div>
  );
};

const ServicesEditor = ({ data, onSave, saving }) => {
  const [services, setServices] = useState(data || DEFAULT_SERVICES);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', desc: '', jobs: '' });

  const startEdit = (key) => {
    const svc = services[key];
    setEditingKey(key);
    setEditForm({ name: svc.name, desc: svc.desc, jobs: (svc.jobs || []).join(', ') });
  };

  const saveEdit = () => {
    const updated = {
      ...services,
      [editingKey]: {
        name: editForm.name,
        desc: editForm.desc,
        jobs: editForm.jobs.split(',').map(j => j.trim()).filter(Boolean)
      }
    };
    setServices(updated);
    setEditingKey(null);
    onSave(updated);
  };

  return (
    <div className="space-y-4" data-testid="services-editor">
      <h2 className="font-heading text-xl font-bold text-white">Services</h2>
      <p className="text-sm text-gray-400">Click on a service to edit its name, description, and jobs.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(services).map(([key, svc]) => {
          const Icon = SERVICE_ICONS[key] || Zap;
          return (
            <div
              key={key}
              className={`card p-5 bg-gray-900 border-gray-800 cursor-pointer hover:border-red-500/50 transition-all ${editingKey === key ? 'border-red-500' : ''}`}
              onClick={() => !editingKey && startEdit(key)}
              data-testid={`service-edit-${key}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Icon className="w-5 h-5 text-red-500" />
                <span className="font-bold text-white">{svc.name}</span>
              </div>
              <p className="text-sm text-gray-400 mb-2">{svc.desc}</p>
              <div className="flex flex-wrap gap-1">
                {(svc.jobs || []).map((job) => (
                  <span key={job} className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded">{job}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Modal */}
      {editingKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full" data-testid="service-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-white">Edit Service</h3>
              <button onClick={() => setEditingKey(null)} className="p-2 hover:bg-gray-900">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Service Name</label>
                <input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="service-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                <input className="input" value={editForm.desc} onChange={(e) => setEditForm({ ...editForm, desc: e.target.value })} data-testid="service-desc-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Jobs (comma separated)</label>
                <textarea className="input min-h-[80px]" value={editForm.jobs} onChange={(e) => setEditForm({ ...editForm, jobs: e.target.value })} data-testid="service-jobs-input" placeholder="Panel Upgrades, Outlet Installation, Rewiring" />
              </div>
              <button onClick={saveEdit} disabled={saving} className="btn-primary w-full" data-testid="save-service-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
                Save Service
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CTAEditor = ({ data, onSave, saving }) => {
  const [form, setForm] = useState({
    title: data?.title || '',
    description: data?.description || '',
    button_text: data?.button_text || ''
  });

  return (
    <div className="card p-6 bg-gray-900 border-gray-800 space-y-4" data-testid="cta-editor">
      <h2 className="font-heading text-xl font-bold text-white">Call To Action Section</h2>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
        <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cta-title-input" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
        <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cta-desc-input" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Button Text</label>
        <input className="input" value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} data-testid="cta-button-input" />
      </div>
      <button onClick={() => onSave(form)} disabled={saving} className="btn-primary" data-testid="save-cta-btn">
        {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
        Save CTA Section
      </button>
    </div>
  );
};

// ===================== ADMIN SETTINGS =====================


export default AdminSiteContent;
